import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { clsx } from "clsx";
import { Vendor } from "../../../types/VendorTypes";
import { OrderForm } from "../../../types/OrderFormTypes";
import { Project, RfxVendor } from "../../../types/ProjectTypes";
import { SpawnDealModal } from "../../project/components/SpawnDealModal";
import { getRelativeTime } from "../../date/DateOp";
import styles from "./VendorDetailContent.module.less";

export interface OrderFormUpdated extends Omit<OrderForm, 'orderStartDate' | 'recordCreationDate' | 'orderEndDate' | 'migrationLength' | 'centsValue' | 'contractType'> {
    orderStartDate?: string;
    orderEndDate?: string;
    centsValue: string;
    estimatedSavingsLowInCents?: number;
    estimatedSavingsHighInCents?: number;
    realizedSavingsInCents?: number;
    parentProjectId?: number;
}

export enum OrderFormStatus {
    UNDER_NEGOTIATION = "under_negotiation",
    EXPIRED = "expired",
    ACTIVE = "active",
    FUTURE = "future",
}

export interface OrderFormWithStatus extends OrderFormUpdated {
    orderFormStatus: OrderFormStatus;
    projectGroupId: number;
    hasChildProject: boolean;
}

interface VendorDetailContentProps {
    vendor: Vendor;
    orderForms: OrderFormWithStatus[];
    afterCreateChildProject: () => Promise<void>;
}

const getNow = () => {
    return Date.now();
}

export const VendorDetailContent: FC<VendorDetailContentProps> = ({ vendor, orderForms, afterCreateChildProject }) => {
    const navigate = useNavigate();
    const [showCreateChildModal, setShowCreateChildModal] = useState(false);
    const [selectedOrderForm, setSelectedOrderForm] = useState<OrderFormWithStatus | null>(null);
    const [childProjectName, setChildProjectName] = useState("");

    const now = getNow();

    const signedContracts = orderForms.filter((of) => of.orderFormStatus !== OrderFormStatus.UNDER_NEGOTIATION);
    const lifetimeValue = signedContracts.reduce((sum, of) => sum + (of.value || 0), 0);
    const lifetimeSavings = signedContracts.reduce((sum, of) => sum + (of.realizedSavingsInCents || 0), 0) / 100;
    const lifetimeStart = new Date(signedContracts.map((of) => of.orderStartDate).filter(Boolean).reduce((acc, v) => Math.min(acc, Date.parse(v as unknown as string)), now)); // TODO: this is a mess

    // Calculate metrics based on active contracts only
    const activeContracts = signedContracts.filter((of) => of.orderFormStatus === OrderFormStatus.ACTIVE);

    const activeValue = activeContracts.reduce((sum, of) => sum + (of.value || 0), 0);
    const avgRiskScore =
        activeContracts.length > 0
            ? activeContracts.reduce((sum, of) => sum + (of.orderScore || 0), 0) /
              activeContracts.length
            : 0;

    const nextRenewal = activeContracts
        .filter((of) => of.orderEndDate)
        .sort(
            (a, b) => new Date(a.orderEndDate!).getTime() - new Date(b.orderEndDate!).getTime()
        )[0];

    const contractGroups = _.chain(orderForms).sortBy([_.property("projectId"), _.property("id")]).groupBy(_.property("projectGroupId")).values().map((orderForms) => {
        const signedContracts = orderForms.filter((of) => of.orderFormStatus !== OrderFormStatus.UNDER_NEGOTIATION);
        const lifetimeValue = signedContracts.length ? signedContracts.reduce((sum, of) => sum + (of.value || 0), 0) : undefined;
        const groupName = signedContracts.length ? _.last(signedContracts)!.name : _.last(orderForms)!.name
        return {
            groupName,
            contractCount: signedContracts.length,
            lifetimeValue,
            orderForms
        };
    }).value();

    const getOrderLink = (orderForm: OrderFormWithStatus) => {
        switch (orderForm.projectId) { // HACK: remap to production environment ids
            case 34871:
                return "https://oasis.infinityloop.ai/project/1065";
            case 34872:
                return "https://oasis.infinityloop.ai/project/1066";
            case 34873:
                return "https://oasis.infinityloop.ai/project/1067";
            case 34948:
                return "https://oasis.infinityloop.ai/project/1068";
        }
        return orderForm.projectId ? `/project/${orderForm.projectId}` : `/order/${orderForm.id}`;
    }

    const formatCurrency = (value: number) => {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}K`;
        }
        return `$${value.toFixed(0)}`;
    };

    const formatDate = (date: Date | string | undefined) => {
        if (!date) return "N/A";
        const d = new Date(date);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const getDaysUntil = (date: Date | string | undefined) => {
        if (!date) return null;
        const d = new Date(date);
        const diffTime = d.getTime() - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleCreateChildClick = (orderForm: OrderFormWithStatus, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click
        setSelectedOrderForm(orderForm);
        setChildProjectName(`${orderForm.name || "Unnamed Contract"}`); // TODO: better heuristic for naming child/renewal project
        setShowCreateChildModal(true);
    };

    const orderFormStatusText = (status: OrderFormStatus): string => {
        switch(status) {
            case OrderFormStatus.UNDER_NEGOTIATION: return "Under Negotiation";
            case OrderFormStatus.EXPIRED: return "Expired";
            case OrderFormStatus.ACTIVE: return "Active";
            case OrderFormStatus.FUTURE: return "-";
            default: return "";
        }
    }

    const orderFormStatusTag = (status: OrderFormStatus) => {
        switch(status) {
            case OrderFormStatus.UNDER_NEGOTIATION: return "vendor-tag-alt-warning";
            case OrderFormStatus.EXPIRED: return "vendor-tag-alt-negative";
            case OrderFormStatus.ACTIVE: return "vendor-tag-alt-positive";
            case OrderFormStatus.FUTURE: return "vendor-tag-alt-positive";
            default: return "vendor-tag-alt-warning";
        }
    }

    return (
        <div className={styles["container"]}>
            <div className={styles["vendor-header-container"]}>
                <div className={styles["vendor-summary-container"]}>
                    <div
                        className={clsx(
                            styles["vendor-summary-card"],
                            activeContracts.length > 0
                                ? styles["vendor-summary-card-positive"]
                                : styles["vendor-summary-card-neutral"]
                        )}
                    >
                        <div className={clsx(styles["label"], styles["label-neutral"])}>
                            Active TCV
                        </div>
                        <div className={styles["card-title-container"]}>
                            <div className={styles["card-title"]}>
                                <span className={styles["truncate"]}>
                                    {activeContracts.length > 0 ? formatCurrency(activeValue) : "-"}
                                </span>
                            </div>
                            {activeContracts.length > 0 && (
                                <div className={styles["card-subtitle"]}>
                                    <span className={styles["card-subtitle"]}>
                                        Across {activeContracts.length} contract
                                        {activeContracts.length !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className={clsx(
                            styles["vendor-summary-card"],
                            signedContracts.length > 0
                                ? styles["vendor-summary-card-positive"]
                                : styles["vendor-summary-card-neutral"]
                        )}
                    >
                        <div className={clsx(styles["label"], styles["label-neutral"])}>
                            Lifetime Contracted Spend
                        </div>
                        <div className={styles["card-title-container"]}>
                            <div className={styles["card-title"]}>
                                <span className={styles["truncate"]}>
                                    {signedContracts.length > 0 ? formatCurrency(lifetimeValue) : "-"}
                                </span>
                            </div>
                            {signedContracts.length > 0 && (
                                <div className={styles["card-subtitle"]}>
                                    <span className={styles["card-subtitle"]}>
                                        {signedContracts.length} contract{signedContracts.length !== 1 ? "s" : ""}{", "}
                                        {getRelativeTime(lifetimeStart, true)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className={clsx(
                            styles["vendor-summary-card"],
                            lifetimeSavings
                                ? styles["vendor-summary-card-positive"]
                                : styles["vendor-summary-card-neutral"]
                        )}
                    >
                        <div className={clsx(styles["label"], styles["label-neutral"])}>
                            Realized Savings
                        </div>
                        <div className={styles["card-title-container"]}>
                            <div className={styles["card-title"]}>
                                <span className={styles["truncate"]}>
                                    {lifetimeSavings ? formatCurrency(lifetimeSavings) : "-"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        className={clsx(
                            styles["vendor-summary-card"],
                            activeContracts.length > 0
                                ? styles["vendor-summary-card-negative"]
                                : styles["vendor-summary-card-neutral"]
                        )}
                    >
                        <div className={clsx(styles["label"], styles["label-neutral"])}>
                            Avg. Risk Score
                        </div>
                        <div className={styles["card-title-container"]}>
                            <div className={styles["card-title"]}>
                                <span className={styles["truncate"]}>
                                    {activeContracts.length > 0
                                        ? `${avgRiskScore.toFixed(1)}%`
                                        : "-"}
                                </span>
                            </div>
                            <div className={styles["card-subtitle"]}>
                                <span className={styles["card-subtitle"]}>
                                    Active portfolio
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        className={clsx(
                            styles["vendor-summary-card"],
                            styles["vendor-summary-card-neutral"]
                        )}
                    >
                        <div className={clsx(styles["label"], styles["label-neutral"])}>
                            Next Renewal
                        </div>
                        <div className={styles["card-title-container"]}>
                            <div className={styles["card-title"]}>
                                <span className={styles["truncate"]}>
                                    {nextRenewal ? formatDate(nextRenewal.orderEndDate) : "N/A"}
                                </span>
                            </div>
                            <div className={styles["card-subtitle"]}>
                                <span className={styles["card-subtitle"]}>
                                    {nextRenewal && getDaysUntil(nextRenewal.orderEndDate)
                                        ? `${getDaysUntil(nextRenewal.orderEndDate)} days away`
                                        : "No upcoming renewals"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles["vendor-container-row"]}>
                <div className={styles["vendor-container"]}>
                    <div className={styles["vendor-section"]}>
                        <div className={styles["vendor-section-header-column"]}>
                            <div className={styles["vendor-section-subheader-row"]}>
                                <p className={styles["vendor-section-title"]}>Contracts</p>
                            </div>
                        </div>

                        <table className={styles["vendor-table"]}>
                            <thead>
                                <tr>
                                    <th>Contract</th>
                                    <th style={{width: 150}}>Status</th>
                                    <th style={{width: 250}}>Term</th>
                                    <th style={{width: 120}}>TCV</th>
                                    <th style={{width: 120}}>Savings (Estimated)</th>
                                    <th style={{width: 120}}>Savings (Realized)</th>
                                    <th style={{width: 120}}>Risk Score</th>
                                </tr>
                            </thead>
                            {orderForms.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{ textAlign: "center", padding: "2rem" }}
                                        >
                                            No contracts found
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                contractGroups.map((contractGroup) => {
                                    return (
                                        <tbody className={styles.contractGroup}>
                                            <tr className={styles.contractGroupHeading}>
                                                <th colSpan={7}>
                                                    <span className={styles.contractGroupName}>{contractGroup.groupName}</span>
                                                    <span className={styles.contractGroupCount}>{contractGroup.contractCount} {contractGroup.contractCount !== 1 ? "contracts" : "contract"}</span>
                                                    {contractGroup.lifetimeValue && (<span className={styles.contractGroupValue}>{formatCurrency(contractGroup.lifetimeValue)} lifetime</span>)}
                                                </th>
                                            </tr>
                                            {contractGroup.orderForms.map((orderForm) => {
                                                const daysUntilRenewal = [OrderFormStatus.ACTIVE].includes(orderForm.orderFormStatus) && getDaysUntil(
                                                    orderForm.orderEndDate
                                                );
                                                const showRenewLink = !orderForm.hasChildProject && daysUntilRenewal && daysUntilRenewal <= 90;
                                                const realizedSavings = orderForm.realizedSavingsInCents !== undefined ? orderForm.realizedSavingsInCents / 100 : undefined;
                                                const baselineValue = realizedSavings ? orderForm.value + realizedSavings : undefined;
                                                const savingsPercent = (realizedSavings && baselineValue) ? ( realizedSavings / baselineValue ) * 100.0 : undefined;
                                                const estimatedSavings = (realizedSavings !== undefined || [OrderFormStatus.UNDER_NEGOTIATION].includes(orderForm.orderFormStatus)) ? orderForm.decrease : undefined;

                                                return (
                                                    <tr
                                                        key={orderForm.id}
                                                        className={clsx(styles.contractRow,[OrderFormStatus.UNDER_NEGOTIATION].includes(orderForm.orderFormStatus) ? styles.contractRowUnderNegotiation : null)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <td>
                                                            <a href={getOrderLink(orderForm)}>
                                                            <div className={clsx(styles["vendor-table-cell"], styles.truncate)}>
                                                                <span
                                                                    className={clsx(
                                                                        styles["vendor-table-cell-name"],
                                                                        styles["vendor-table-position"]
                                                                    )}
                                                                >
                                                                    {orderForm.name || "Unnamed Contract"}
                                                                </span>
                                                            </div>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span
                                                                    className={clsx(
                                                                        styles["vendor-tag"],
                                                                        styles[orderFormStatusTag(orderForm.orderFormStatus)]
                                                                    )}
                                                                >
                                                                    {orderFormStatusText(orderForm.orderFormStatus)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span>
                                                                    {formatDate(orderForm.orderStartDate)}{" - "}{formatDate(orderForm.orderEndDate)}
                                                                </span>
                                                                {daysUntilRenewal && (
                                                                    <span
                                                                        className={
                                                                            styles[
                                                                                "vendor-table-cell-description"
                                                                            ]
                                                                        }
                                                                    >
                                                                        {daysUntilRenewal > 0
                                                                            ? `${daysUntilRenewal} days`
                                                                            : `${Math.abs(daysUntilRenewal)} days ago`}{" "}
                                                                        {showRenewLink && (<a onClick={(e) => handleCreateChildClick(orderForm, e)}>Ready to renew?</a>)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span>
                                                                    {orderForm.value ? formatCurrency(orderForm.value) : "-"}
                                                                </span>
                                                                {baselineValue && (
                                                                    <span
                                                                        className={
                                                                            styles[
                                                                                "vendor-table-cell-description"
                                                                            ]
                                                                        }
                                                                    >
                                                                        from {formatCurrency(baselineValue)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span>
                                                                    {estimatedSavings ? formatCurrency(estimatedSavings) : "-"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span
                                                                    className={clsx(
                                                                        styles["vendor-table-cell-name"],
                                                                        realizedSavings &&
                                                                            realizedSavings > 0
                                                                            ? styles[
                                                                                "vendor-table-cell-name-positive"
                                                                            ]
                                                                            : ""
                                                                    )}
                                                                >
                                                                    {realizedSavings ? formatCurrency(realizedSavings) : "-"}
                                                                </span>
                                                                {(savingsPercent && (
                                                                        <span
                                                                            className={
                                                                                styles[
                                                                                    "vendor-table-cell-description"
                                                                                ]
                                                                            }
                                                                        >
                                                                            {savingsPercent.toFixed(1)}% of value
                                                                        </span>
                                                                    )) ||
                                                                    ""}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles["vendor-table-cell"]}>
                                                                <span
                                                                    className={clsx(
                                                                        styles["vendor-tag"],
                                                                        styles["vendor-tag-alt-negative"]
                                                                    )}
                                                                >
                                                                    {orderForm.orderScore?.toFixed(1) ||
                                                                        "0.0"}
                                                                    %
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    );
                                })
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {showCreateChildModal && (
                <SpawnDealModal
                    open={showCreateChildModal}
                    rfxProject={{name: childProjectName} as Project} // HACK!
                    vendor={{vendorName: "Renewal", id: -99999} as RfxVendor} // HACK
                    onClose={() => { setShowCreateChildModal(false); afterCreateChildProject(); }}
                    onSuccess={afterCreateChildProject}
                />
            )}
        </div>
    );
};
